import { create } from 'zustand';
import type { PageAssignment } from '../api/endpoints';
import { pmGetAssignments } from '../api/endpoints';

interface AssignmentState {
  /** All assignments for the current project (owner sees all, annotator sees own) */
  assignments: PageAssignment[];
  loaded: boolean;

  /** Load assignments from backend */
  load: (projectId: string) => Promise<void>;
  clear: () => void;

  /** Check if a page is assigned to anyone */
  getAssignment: (file: string, page: number) => PageAssignment | undefined;
  /** Get all assignments for a given file */
  getFileAssignments: (file: string) => PageAssignment[];
  /** Get unique assignees for a file (username list) */
  getFileAssignees: (file: string) => string[];
  /** Set of assigned page keys ("file:page") for the current user (annotator filtering) */
  myPageKeys: () => Set<string>;
}

export const useAssignmentStore = create<AssignmentState>((set, get) => ({
  assignments: [],
  loaded: false,

  load: async (projectId: string) => {
    try {
      const data = await pmGetAssignments(projectId);
      if (data) {
        set({ assignments: data, loaded: true });
      }
    } catch {
      set({ assignments: [], loaded: true });
    }
  },

  clear: () => set({ assignments: [], loaded: false }),

  getAssignment: (file, page) =>
    get().assignments.find((a) => a.file === file && a.page === page),

  getFileAssignments: (file) =>
    get().assignments.filter((a) => a.file === file),

  getFileAssignees: (file) => {
    const names = new Set<string>();
    for (const a of get().assignments) {
      if (a.file === file && a.username) names.add(a.username);
    }
    return [...names];
  },

  myPageKeys: () => {
    const keys = new Set<string>();
    for (const a of get().assignments) {
      keys.add(`${a.file}:${a.page}`);
    }
    return keys;
  },
}));
